"use client";
import { useGlobalContext } from "@/app/context";
import { InviteService, UserService } from "@/app/service";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Inbox, User, Play } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import FeedbackPopup from "../ui/FeedbackPopup";
import Tooltip from "../ui/Tooltip";
import UserGreeting from "../ui/UserGreeting/UserGreeting";
import BurgerMenu from "./BurgerMenu";
import CommentateToggleBar from "./CommentateToggleBar";

export default function Header() {
    const router = useRouter();
    const { userType, setUserType, userData, setUserData } = useGlobalContext();
    const { user, signOut, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [unacceptedInviteCount, setUnacceptedInviteCount] = useState(0);

    // Fetch user data when authenticated
    useEffect(() => {
        const fetchUserData = async () => {
            const user = await UserService.getCurrentUser()
            if (user) {
                setUserData(user);
            }
        };

        if (authStatus === "authenticated" && !userData) {
            fetchUserData().catch((error) => {
                console.error("Error in fetchUserData useEffect:", error);
            });
        }
    }, [authStatus, userData, setUserData]);

    // Fetch unaccepted invites count
    useEffect(() => {
        const fetchInviteCount = async () => {
            try {
                const invites = await InviteService.getStageInvites();
                const unacceptedCount = invites.filter(invite => !invite.inviteAccepted).length;
                setUnacceptedInviteCount(unacceptedCount);
            } catch (error) {
                console.error("Error fetching invite count:", error);
                setUnacceptedInviteCount(0);
            }
        };

        if (authStatus === "authenticated") {
            fetchInviteCount();

            // Poll for updates every 60 seconds
            const interval = setInterval(fetchInviteCount, 60000);
            return () => clearInterval(interval);
        }
    }, [authStatus]);

    return (
        <>
            <div className="w-screen items-center justify-between border-b border-widertalk-cream/50 bg-widertalk-cream flex">
                <div className="flex items-center gap-2 p-4 px-5">
                    <Image
                        className="cursor-pointer"
                        onClick={() => router.push("/home")}
                        src="/logo.svg"
                        alt="Logo"
                        width={40}
                        height={40}
                        style={{ width: '40px', height: 'auto' }}
                        priority
                    />
                    <span
                        className="text-lg sm:text-2xl font-bold cursor-pointer select-none"
                        style={{ fontFamily: 'Gendy, sans-serif' }}
                        onClick={() => router.push("/home")}
                    >
                        WiderTalk
                    </span>
                </div>
                <div className="flex items-center gap-3 pr-5">
                    {authStatus === "unauthenticated" && (
                        <button
                            onClick={() => router.push("/login")}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-widertalk-navy text-white text-sm font-medium rounded-lg hover:bg-widertalk-navy/90 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-widertalk-navy/50 focus:ring-offset-2 transform hover:scale-105 active:scale-95"
                            title="Login to your account"
                        >
                            <User size={16} className="sm:hidden" />
                            <span className="hidden sm:inline">Login</span>
                            <span className="sm:hidden">Log In</span>
                        </button>
                    )}

                    {authStatus === "authenticated" && (
                        <>
                            <Tooltip text="Playback" position="bottom">
                                <button
                                    onClick={() => router.push("/playback")}
                                    className="inline-flex items-center justify-center rounded-lg p-2 w-10 h-10 hover:bg-widertalk-navy/20 text-widertalk-navy transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2 cursor-pointer"
                                    title="On Demand"
                                >
                                    <Play size={20} />
                                </button>
                            </Tooltip>
                            <div className="relative">
                                <Tooltip text="See invites" position="bottom">
                                    <button
                                        onClick={() => router.push("/invites")}
                                        className="inline-flex items-center justify-center rounded-lg p-2 w-10 h-10 hover:bg-widertalk-navy/20 text-widertalk-navy transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2 cursor-pointer"
                                        title="See invites"
                                    >
                                        <Inbox size={20} />
                                    </button>
                                </Tooltip>
                                {unacceptedInviteCount > 0 && (
                                    <span className="absolute inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full -top-1 -right-1 min-w-[20px]">
                                        {unacceptedInviteCount > 99 ? '99+' : unacceptedInviteCount}
                                    </span>
                                )}
                            </div>
                            <Tooltip text="Edit Profile" position="bottom">
                                <button
                                    onClick={() => router.push("/profile")}
                                    className="inline-flex items-center justify-center rounded-lg p-2 w-10 h-10 hover:bg-widertalk-navy/20 text-widertalk-navy transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2 cursor-pointer overflow-hidden"
                                    title="Edit Profile"
                                >
                                    {userData?.avatarUrl ? (
                                        <Image
                                            src={userData.avatarUrl}
                                            alt="Profile"
                                            width={24}
                                            height={24}
                                            className="rounded-full object-cover"
                                            style={{ width: '24px', height: '24px' }}
                                        />
                                    ) : (
                                        <User size={20} />
                                    )}
                                </button>
                            </Tooltip>
                        </>
                    )}

                    {/* Burger Menu - Always visible, moved to final position */}
                    <BurgerMenu onFeedbackClick={() => setShowFeedbackPopup(true)} />
                </div>
            </div>

            {/* Feedback Popup */}
            {showFeedbackPopup && (
                <FeedbackPopup onClose={() => setShowFeedbackPopup(false)} />
            )}

            {/* Commentate Toggle Bar */}
            <CommentateToggleBar />
        </>
    );
}
